import { isEqual } from 'lodash'
import React, {
  DetailedHTMLProps,
  DOMAttributes,
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useMount, useUnmount } from './hooks/useMount'
import { useParticipantsChange } from './hooks/useParticipantsChange'
import { useZoomSession } from './SessionProvider'
import { SessionVideoContainer } from './SessionVideoContainer'

/**
 * Custom element type definition for Zoom Video SDK components
 * Extends standard DOM attributes with children support
 */
type CustomElement<T> = Partial<T & DOMAttributes<T> & { children: any }>

/**
 * Global type declarations for Zoom Video SDK integration
 * - Window.zoomLeaving: Global flag to track cleanup state across component remounts
 * - JSX elements: Custom video player components from Zoom SDK
 */
declare global {
  interface Window {
    zoomLeaving: boolean
  }
  namespace JSX {
    interface IntrinsicElements {
      ['video-player']: DetailedHTMLProps<HTMLAttributes<any>, any> & {
        class?: string
      }
      ['video-player-container']: CustomElement<any> & { class?: string }
    }
  }
}

/**
 * This function polls to check if any previous instance of zoom is still getting
 * cleaned up and waits for that process to complete if it's found.  This has a
 * max 1 second waiting period to handle any possible cases where the cleanup signal
 * gets lost for some reason
 *
 * This function relies on our zoom session cleanup logic setting a global flag
 * 'zoomLeaving' when it begins its logic and clearing that flag when it has
 * completed that work.  That allows this functionality to include covering even
 * when we've unmounted & remounted this component (where state, etc would have
 * been reset)
 */
export const waitForZoomCleanup = async () => {
  let timer: ReturnType<typeof setInterval> | undefined
  let timeout: ReturnType<typeof setTimeout> | undefined
  await new Promise<void>(resolve => {
    timeout = setTimeout(() => {
      window.zoomLeaving = false
      if (timer) clearInterval(timer)

      resolve()
    }, 1000)
    timer = setInterval(() => {
      if (!window.zoomLeaving) {
        if (timer) clearInterval(timer)
        if (timeout) clearTimeout(timeout)

        resolve()
      }
    }, 100)
  })
}

/**
 * SessionCallV2 Component
 *
 * Main component for managing Zoom video call sessions within the CRM appointment system.
 * Handles the complete lifecycle of a Zoom session including initialization, participant management,
 * network quality monitoring, and cleanup.
 *
 *
 */
export const SessionCallV2: React.FC = () => {
  const {
    appointment,
    client,
    sessionName,
    token,
    isVideoOn,
    setIsVideoOn,
    setScreen,
    me,
    logger,
  } = useZoomSession()
  const appointmentId = appointment.id
  const appointmentMemberId = appointment.patient.toString()

  // Core Zoom session state management
  const [stream, setStream] = useState<any | null>(null) // Zoom media stream instance
  const [currentUser, setCurrentUser] = useState<any | null>(null) // Current user participant info
  const [allSessionParticipants, setAllSessionParticipants] = useState<any[]>([]) // Other session participants
  const [isInitialized, setIsInitialized] = useState(false) // Session initialization status

  // Network quality tracking for all participants
  const [networkQuality, setNetworkQuality] = useState<Record<number, Record<keyof any, number>>>(
    {},
  )

  /**
   * Comprehensive Zoom session cleanup handler
   *
   * Ensures proper cleanup of all Zoom resources and state to prevent memory leaks
   * and conflicts with future sessions. Uses a global flag to coordinate cleanup
   * across component remounts.
   *
   * Cleanup sequence:
   * 1. Set global cleanup flag to prevent new sessions
   * 2. Clear stream and initialization state
   * 3. Stop all media streams (video/audio)
   * 4. Detach video from current user
   * 5. Leave the Zoom session
   * 6. Clear global cleanup flag
   *
   * Error handling: Gracefully handles already-stopped streams while ensuring
   * the client.leave() call always executes.
   */
  const handleCleanup = useCallback(async () => {
    logger.info('Cleaning up Zoom Session')
    window.zoomLeaving = true

    try {
      /**
       * Critical: Reset state before nulling stream to prevent re-initialization
       */
      setStream(null)
      setIsInitialized(false)

      /**
       * Graceful media cleanup with error suppression
       * These operations may fail if streams are already stopped, but we want
       * to ensure client.leave() always executes regardless of media state
       */
      await Promise.all([
        currentUser ? stream?.detachVideo(currentUser?.userId).catch(() => null) : null,
        stream?.stopVideo().catch(() => null),
        stream?.stopAudio().catch(() => null),
      ])
      setCurrentUser(null)
      await client.leave()
    } catch (e) {
      logger.warn('handleCleanup', e as any)
      throw e
    } finally {
      // Always clear the global flag, even if cleanup fails
      window.zoomLeaving = false
      logger.info('Zoom Session clean up complete')
    }
  }, [client, currentUser, stream])

  /**
   * User-initiated call termination handler
   *
   * Handles the complete flow when a user chooses to leave the call:
   * 1. Performs full session cleanup
   * 2. Returns user to pre-call screen
   *
   * @returns Promise that resolves when leave process is complete
   */
  const handleLeaveCall = useCallback(async () => {
    logger.info('Leaving Zoom Session')
    await handleCleanup()
    setScreen('precall')
    logger.info('Left Zoom Session')
  }, [handleCleanup, setScreen])

  /**
   * Safety mechanism: Auto-cleanup if appointment becomes invalid
   * Prevents orphaned sessions when appointment data is corrupted or missing
   */
  useEffect(() => {
    if (!appointmentId || !appointmentMemberId) handleCleanup()
  }, [appointmentId, appointmentMemberId, handleCleanup])

  /**
   * Participant change event handler
   *
   * Updates current user and participant info when their state changes (e.g., video on/off)
   */
  useParticipantsChange(client, updatedParticipants => {
    // Guard: Ensure current user is properly initialized
    if (currentUser === null || currentUser.userId === null) return

    logger.info('Zoom: Handling participant change')

    // Find and update current user info if it has changed
    const refreshedCurrentUserInfo = updatedParticipants.find(
      user => user.userId === currentUser.userId,
    )

    // Update current user state if video status or other properties changed
    if (
      refreshedCurrentUserInfo &&
      refreshedCurrentUserInfo?.bVideoOn != null &&
      !isEqual(currentUser, refreshedCurrentUserInfo)
    ) {
      logger.info('Zoom: Previous user info', currentUser)
      logger.info('Zoom: Refreshing current user', refreshedCurrentUserInfo)
      setCurrentUser(refreshedCurrentUserInfo)
    }

    // Update participants list (excluding appointment member, sorted by userId)
    // Filter out the appointment member to avoid duplicate display
    // Sort by userId in descending order for consistent participant ordering
    // This section is meant to handle videos for one or more providers that join
    // the session.
    // However, if the member joins twice - their second video will show up here.
    updatedParticipants = updatedParticipants
      .filter(user => user.userGuid !== null && user.userGuid !== undefined)
      .sort((user1, user2) => Number(user2.userId) - Number(user1.userId))
    logger.info('Updated participants', updatedParticipants)
    setAllSessionParticipants(updatedParticipants)
  })

  /**
   * Zoom session initialization handler
   *
   * Manages the complete initialization sequence for a new Zoom session:
   * 1. Waits for any previous session cleanup to complete
   * 2. Joins the Zoom session with user and appointment credentials
   * 3. Initializes media stream and user info
   *
   * @returns Promise that resolves when initialization is complete
   * @throws Error if initialization fails at any step
   */
  const handleInitStream = useCallback(async () => {
    try {
      logger.info('Initializing Zoom Session')

      // Wait for any previous session cleanup to complete
      await waitForZoomCleanup()

      // Join session with user and appointment identifiers
      // Parameters: sessionName, token, userId, userName (using appointment ID as userName)
      await client.join(sessionName, token, me.id.toString(), appointment.id.toString())

      // Initialize core session objects after successful join
      // Get the media stream instance for video/audio operations
      setStream(client.getMediaStream())
      // Get current user info including video/audio state
      setCurrentUser(client.getCurrentUserInfo())
      // Mark session as fully initialized and ready for video operations
      setIsInitialized(true)

      logger.info('Zoom Session Initialized')
    } catch (e) {
      logger.warn('handleInitStream', e as any)
      throw e
    }
  }, [client, sessionName, token, me.id, appointment.id])

  /**
   * Derived network quality calculator
   *
   * Computes the effective network quality for each participant by taking
   * the minimum of their uplink and downlink quality scores. This provides
   * a conservative estimate of connection quality that reflects the worst
   * performing direction.
   *
   * @returns Record mapping userId to their effective network quality score
   */
  const derivedNetworkQuality = useMemo(() => {
    const qualities: Record<number, number> = {}
    for (const sessionParticipant of allSessionParticipants) {
      const quality = networkQuality[sessionParticipant.userId]
      if (!quality) continue
      if (!('uplink' in quality) && !('downlink' in quality)) continue
      // Use the lower of uplink/downlink as the effective quality
      if (sessionParticipant.userGuid) {
        qualities[sessionParticipant.userGuid] = Math.min(
          quality.downlink ?? Infinity,
          quality.uplink ?? Infinity,
        )
      }
    }
    return qualities
  }, [networkQuality, allSessionParticipants])

  /**
   * Network quality change event handler
   *
   * Processes real-time network quality updates from the Zoom SDK.
   * Updates the quality tracking state and forwards events to the
   * event handling system for logging/analytics.
   *
   * @param e Network quality change event from Zoom SDK
   */
  const handleNetworkQualityChange = (e: any) => {
    // Update network quality state for the specific user and quality type
    setNetworkQuality(state => {
      const prev = state[e.userId] ?? {}
      prev[e.type] = e.level
      return { ...state, [e.userId]: prev }
    })
  }

  /**
   * Component mount effect
   *
   * Initializes the Zoom session and sets up event listeners when the component mounts.
   * This is the entry point for starting a new video call session.
   */
  useMount(() => {
    handleInitStream()
    client.on('network-quality-change', handleNetworkQualityChange)
  })

  /**
   * Component unmount effect
   *
   * Performs cleanup when the component unmounts to prevent memory leaks
   * and ensure proper session termination. Removes event listeners and
   * cleans up all Zoom resources.
   */
  useUnmount(() => {
    handleCleanup()
    client.off('network-quality-change', handleNetworkQualityChange)
  })

  /**
   * Conditional rendering of the video call interface
   *
   * Only renders the SessionVideoContainer when all required conditions are met:
   * - Current user is properly initialized with a valid userId
   * - Media stream is available
   * - Session initialization is complete
   *
   * Returns null during initialization to prevent rendering with incomplete state.
   *
   * @returns SessionVideoContainer component or null if not ready
   */
  return currentUser && currentUser.userId && stream && isInitialized ? (
    <SessionVideoContainer
      currentUser={currentUser}
      setCurrentUser={setCurrentUser}
      client={client}
      stream={stream}
      setIsVideoOn={setIsVideoOn}
      isVideoOn={isVideoOn}
      allSessionParticipants={allSessionParticipants}
      handleLeaveCall={handleLeaveCall}
      derivedNetworkQuality={derivedNetworkQuality}
    />
  ) : null
}
